import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/shared-client/components/ui'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFRichTextViewer from '@web/src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import GroupDelete from '@web/src/features/group/group-delete/group-delete'
import { GroupEventsList } from '@web/src/features/group/group-events-list/group-events-list'
import GroupTransferModal from '@web/src/features/group/group-transfer-modal/group-transfer-modal'
import { getMembershipBadge, getVisibilityBadge } from '@web/src/features/group/utils/group-badges'
import Posts from '@web/src/features/post/posts/posts'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { limitBadgeNumber } from '@web/src/lib/utilsClient'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  CalendarCheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisIcon,
  MessageCircleIcon,
  MessageCircleQuestionIcon,
  MessageSquareIcon,
  UserCheckIcon,
  UserPlusIcon,
  UsersIcon,
  UserXIcon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  AcceptGroupInvitationMutation,
  AcceptGroupInvitationMutationVariables,
  CancelJoinRequestMutation,
  CancelJoinRequestMutationVariables,
  GroupQuery,
  GroupQueryVariables,
  JoinGroupMutation,
  JoinGroupMutationVariables,
  LeaveGroupMutation,
  LeaveGroupMutationVariables,
} from 'types/graphql'
import { groupPageHeadFromLoaderData } from './$groupIdOrSlug.seo'

dayjs.extend(relativeTime)

const GROUP_QUERY: TypedDocumentNode<GroupQuery, GroupQueryVariables> = gql`
  query GroupQuery($groupIdOrSlug: String!) {
    group: group(groupIdOrSlug: $groupIdOrSlug) {
      id
      createdAt
      slug
      name
      description
      tagLine
      ownerId
      visibility
      groupType
      joinApproval
      memberCount
      postCount
      coverNsfwRating
      coverImageUrlMedium
      coverImageOffsetX
      coverImageOffsetY
      canJoin
      canPost
      canLeave
      canInvite
      canEdit
      canModerate
      canCreateEvent
      canReport
      postApproval
      postPendingApprovalCount
      joinRequestCounts {
        totalCount
        pendingCount
        approvedCount
        rejectedCount
      }
      myMembership {
        id
        role
        status
        mutedUntil
      }
      myPendingJoinRequest {
        id
        status
        createdAt
      }
      myPendingInvitation {
        id
        status
        createdAt
      }
      owner {
        ...ProfileBasicInfo
      }
      tags {
        id
        name
      }
    }
  }

  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const JOIN_GROUP_MUTATION: TypedDocumentNode<JoinGroupMutation, JoinGroupMutationVariables> = gql`
  mutation JoinGroupMutation($groupId: String!) {
    joinGroup(id: $groupId)
  }
`

const LEAVE_GROUP_MUTATION: TypedDocumentNode<LeaveGroupMutation, LeaveGroupMutationVariables> = gql`
  mutation LeaveGroupMutation($groupId: String!) {
    leaveGroup(id: $groupId)
  }
`

const CANCEL_JOIN_REQUEST_MUTATION: TypedDocumentNode<CancelJoinRequestMutation, CancelJoinRequestMutationVariables> =
  gql`
    mutation CancelJoinRequestMutation($id: String!) {
      cancelJoinRequest(id: $id)
    }
  `

const ACCEPT_GROUP_INVITATION_MUTATION: TypedDocumentNode<
  AcceptGroupInvitationMutation,
  AcceptGroupInvitationMutationVariables
> = gql`
  mutation AcceptGroupInvitationMutation($id: String!, $accept: Boolean!) {
    acceptGroupInvitation(id: $id, accept: $accept) {
      id
      status
    }
  }
`

export const Route = createFileRoute('/(public)/(main-layout)/group/$groupIdOrSlug')({
  component: GroupPage,
  head: ({ loaderData }) => groupPageHeadFromLoaderData(loaderData),
  loader: async ({ context, params }) => {
    const { queryData: groupData, queryRef: groupQueryRef } = await queryAndPreload(context, GROUP_QUERY, {
      variables: { groupIdOrSlug: params.groupIdOrSlug },
    })
    return {
      queryRef: groupQueryRef,
      groupQueryData: groupData,
      group: groupData?.group,
    }
  },
})

function GroupPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const client = useApolloClient()
  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<GroupQuery>)
  const navigate = useNavigate()
  const search = useSearch({ strict: false })
  const { hasPermission, isAuthenticated } = useAuth()
  const [activeTab, setActiveTab] = useState((search as { activeTab?: string })?.activeTab || 'posts')
  const [transferModal, setTransferModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [leaveModal, setLeaveModal] = useState(false)
  const [declineInvitationModal, setDeclineInvitationModal] = useState(false)
  const [reportModal, setReportModal] = useState(false)

  const group = (data ?? loaderData.groupQueryData)?.group ?? loaderData.group ?? null
  const [detailsExpanded, setDetailsExpanded] = useState(!group?.myMembership)

  // Refs for tab triggers
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  // Create a refetch function that uses Apollo Client directly to avoid router reloads
  const handleRefetch = async () => {
    await client.refetchQueries({ include: [GROUP_QUERY] })
  }

  const [joinGroupMutation, { loading: joinLoading }] = useMutation(JOIN_GROUP_MUTATION, {
    onCompleted: () => {
      toast.success(t('Joined group request successful'))
      handleRefetch()
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const [leaveGroupMutation, { loading: leaveLoading }] = useMutation(LEAVE_GROUP_MUTATION, {
    onCompleted: () => {
      toast.success(t('Left group successfully'))
      navigate({ to: '/group/list' })
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const [cancelJoinRequestMutation, { loading: cancelRequestLoading }] = useMutation(CANCEL_JOIN_REQUEST_MUTATION, {
    onCompleted: () => {
      toast.success(t('Join request cancelled'))
      handleRefetch()
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const [acceptGroupInvitationMutation, { loading: acceptInvitationLoading }] = useMutation(
    ACCEPT_GROUP_INVITATION_MUTATION,
    {
      onCompleted: () => {
        if (group?.joinApproval) {
          toast.success(t('Your join request is pending approval.'))
        } else {
          toast.success(t('You are now a member of the group.'))
        }
        handleRefetch()
      },
      onError: (err) => {
        toast.error(err.message)
      },
    }
  )

  const [rejectGroupInvitationMutation, { loading: rejectInvitationLoading }] = useMutation(
    ACCEPT_GROUP_INVITATION_MUTATION,
    {
      onCompleted: () => {
        toast.success(t('Invitation declined'))
        navigate({ to: '/group/list' })
      },
      onError: (err) => {
        toast.error(err.message)
      },
    }
  )

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!group) {
    return <ErrorAlert error={new Error(t('Group not found'))} />
  }

  const handleJoinGroup = () => {
    joinGroupMutation({
      variables: {
        groupId: group.id,
      },
    })
  }

  const handleLeaveGroup = () => {
    setLeaveModal(false)
    leaveGroupMutation({
      variables: {
        groupId: group.id,
      },
    })
  }

  const handleCancelJoinRequest = () => {
    if (group.myPendingJoinRequest?.id) {
      cancelJoinRequestMutation({
        variables: {
          id: group.myPendingJoinRequest.id,
        },
      })
    }
  }

  const handleAcceptInvitation = () => {
    if (group.myPendingInvitation?.id) {
      acceptGroupInvitationMutation({
        variables: {
          id: group.myPendingInvitation.id,
          accept: true,
        },
      })
    }
  }

  const handleRejectInvitation = () => {
    setDeclineInvitationModal(false)
    if (group.myPendingInvitation?.id) {
      rejectGroupInvitationMutation({
        variables: {
          id: group.myPendingInvitation.id,
          accept: false,
        },
      })
    }
  }

  const handleEditGroup = () => {
    navigate({ to: '/group/$groupIdOrSlug/edit', params: { groupIdOrSlug: group.slug } } as never)
  }

  const handleTransferOwnership = () => {
    setTransferModal(true)
  }

  const handleDeleteGroupMenu = () => {
    setDeleteModal(true)
  }

  const handleLeaveGroupMenu = () => {
    setLeaveModal(true)
  }

  const handleCoverImageMenu = () => {
    navigate({ to: '/group/$groupIdOrSlug/cover-image', params: { groupIdOrSlug: group.slug } } as never)
  }

  const handleTabChange = (tabValue: string | number | null) => {
    const next = String(tabValue ?? 'posts')
    setActiveTab(next)

    // Update the browser url without reloading
    const searchParams = new URLSearchParams()
    searchParams.set('activeTab', next)
    window.history.replaceState(null, '', window.location.pathname + '?' + searchParams.toString())
  }

  const isOwner = group.myMembership?.role === 'OWNER' || hasPermission('group:read:all')

  // Determine which button to show based on priority
  const renderJoinButton = () => {
    if (!isAuthenticated || group.myMembership) {
      return null
    }

    if (group.myPendingJoinRequest?.status === 'PENDING') {
      return (
        <Button
          className="bg-orange-600 text-white hover:bg-orange-600/90"
          disabled={cancelRequestLoading}
          onClick={handleCancelJoinRequest}
          type="button"
          variant="default"
        >
          {cancelRequestLoading ? <Spinner className="size-4" /> : <UserXIcon className="size-4" />}
          {t('Cancel Join Request')}
        </Button>
      )
    }

    if (group.myPendingInvitation?.status === 'PENDING') {
      return (
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            disabled={rejectInvitationLoading}
            onClick={() => setDeclineInvitationModal(true)}
            size="sm"
            type="button"
            variant="outline"
          >
            {rejectInvitationLoading ? <Spinner className="size-4" /> : <UserXIcon className="size-4" />}
            {t('Decline Invite')}
          </Button>
          <Button
            disabled={acceptInvitationLoading}
            onClick={handleAcceptInvitation}
            size="sm"
            type="button"
            variant="default"
          >
            {acceptInvitationLoading ? <Spinner className="size-4" /> : <UserCheckIcon className="size-4" />}
            {t('Accept Invite')}
          </Button>
        </div>
      )
    }

    if (group.canJoin) {
      return (
        <Button disabled={joinLoading} onClick={handleJoinGroup} type="button" variant="default">
          {joinLoading ? <Spinner className="size-4" /> : <UserPlusIcon className="size-4" />}
          {group.joinApproval ? t('Request to Join') : t('Join Group')}
        </Button>
      )
    }
    return null
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-2 pt-2">
      <div className="flex w-full flex-col gap-3 py-4">
        <PageBreadcrumb
          items={[
            { label: t('Home'), to: '/' },
            { label: t('Discover Groups'), to: '/group/list' },
            { label: group.name },
          ]}
        />

        <Card className="overflow-hidden p-0">
          <AspectRatio className="max-h-[200px] w-full" ratio={2.35}>
            <SFMediaImage
              backgroundPosition={`center ${group.coverImageOffsetY || 50}%`}
              backgroundSize="cover"
              className="h-full w-full"
              imageProps={{
                alt: t('{{groupName}} — cover image', { groupName: group.name }),
              }}
              media={{
                imageUrlMedium: group.coverImageUrlMedium,
                ownerId: group.ownerId,
                nsfwRating: group.coverNsfwRating,
              }}
              missingImage="/missing-group.webp"
            />
          </AspectRatio>
        </Card>

        <Collapsible onOpenChange={(open) => setDetailsExpanded(open)} open={detailsExpanded}>
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-row items-start justify-between gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex w-full justify-between gap-2">
                  <CollapsibleTrigger
                    render={
                      <Button
                        className="h-auto min-w-0 flex-1 justify-start border-0 bg-transparent p-0 text-left hover:bg-transparent"
                        size="lg"
                        type="button"
                        variant="ghost"
                      />
                    }
                  >
                    <span className="flex max-w-[75dvw] items-center gap-2 overflow-hidden">
                      <h1 className="inline-block truncate font-bold text-2xl">{group.name}</h1>
                      {detailsExpanded ? (
                        <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                      )}
                    </span>
                  </CollapsibleTrigger>
                  {(group.canEdit || group.canLeave || isOwner || group.canReport) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            aria-label={t('Group options')}
                            className="shrink-0"
                            size="icon"
                            type="button"
                            variant="outline"
                          />
                        }
                      >
                        <EllipsisIcon className="size-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {group.canEdit ? (
                          <DropdownMenuItem onClick={handleEditGroup}>{t('Edit Group')}</DropdownMenuItem>
                        ) : null}
                        {group.canEdit ? (
                          <DropdownMenuItem onClick={handleCoverImageMenu}>{t('Edit Cover Image')}</DropdownMenuItem>
                        ) : null}
                        {group.canLeave ? (
                          <DropdownMenuItem onClick={handleLeaveGroupMenu}>{t('Leave Group')}</DropdownMenuItem>
                        ) : null}
                        {group.canReport ? (
                          <DropdownMenuItem onClick={() => setReportModal(true)}>{t('Report')}</DropdownMenuItem>
                        ) : null}
                        {isOwner ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleTransferOwnership}>
                              {t('Transfer Ownership')}
                            </DropdownMenuItem>
                          </>
                        ) : null}
                        {isOwner ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleDeleteGroupMenu} variant="destructive">
                              Delete Group
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {group.tagLine ? <h2 className="line-clamp-2 text-lg">{group.tagLine}</h2> : null}

                <div className="flex flex-wrap gap-1">
                  {getVisibilityBadge(group.visibility, group.groupType)}
                  {group.myMembership ? getMembershipBadge(group.myMembership) : null}
                  {group.tags?.map((tag) => (
                    <Badge key={tag.id} variant="outline">
                      {tag.name}
                    </Badge>
                  ))}
                </div>

                <CollapsibleContent className="w-full">
                  {group.description ? (
                    <Card className="w-full py-0">
                      <CardContent className="flex flex-col gap-3 px-3 py-3">
                        <SFProfileBanner
                          lowerTitle={<span className="line-clamp-1 text-sm">{t('Group Owner')}</span>}
                          profile={group.owner}
                          size="md"
                        />

                        <Separator />

                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center gap-2">
                            <UsersIcon className="size-5" />
                            <span className="line-clamp-1">{t('{{count}} Members', { count: group.memberCount })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MessageSquareIcon className="size-5" />
                            <span className="line-clamp-1">{t('{{count}} Posts', { count: group.postCount })}</span>
                          </div>
                        </div>

                        <Separator />

                        <SFRichTextViewer
                          className="w-full border-none p-0"
                          content={group.description}
                          editorStyles="overflow: hidden"
                        />
                      </CardContent>
                    </Card>
                  ) : null}
                </CollapsibleContent>
              </div>
            </div>
          </div>
        </Collapsible>

        {(group.myMembership || group.groupType === 'PUBLIC' || hasPermission('group:read:all')) && isAuthenticated && (
          <div className="flex w-full flex-col items-start gap-2">
            <Separator className="w-full" />

            <div className="flex w-full flex-wrap justify-center gap-2 md:justify-start">
              <Button
                className="gap-1.5 px-6"
                onClick={() =>
                  navigate({ to: '/group/$groupIdOrSlug/members', params: { groupIdOrSlug: group.slug } } as never)
                }
                type="button"
                variant="outline"
              >
                <UsersIcon />
                {t('Members')}
                {group.canEdit && group.joinRequestCounts.pendingCount > 0 ? (
                  <Badge size="sm">{limitBadgeNumber(group.joinRequestCounts.pendingCount)}</Badge>
                ) : null}
              </Button>

              {group?.canInvite ? (
                <Button
                  className="gap-1.5 px-10"
                  onClick={() =>
                    navigate({ to: '/group/$groupIdOrSlug/invite', params: { groupIdOrSlug: group.slug } } as never)
                  }
                  type="button"
                >
                  <UserPlusIcon className="size-4" />
                  {t('Invite')}
                </Button>
              ) : null}
            </div>

            <Separator className="w-full" />
          </div>
        )}

        {!group.myMembership && (
          <Card>
            <CardContent className="flex flex-col gap-4">
              {group.myPendingJoinRequest?.status === 'PENDING' ? (
                <p className="text-center text-muted-foreground">
                  {t('Your join request or invitation needs to be approved by a group admin.')}
                </p>
              ) : (
                <p className="text-center text-muted-foreground">
                  {group.groupType === 'PUBLIC'
                    ? t('You must be a group member to participate in the group')
                    : t('You must be a group member to view group content')}
                </p>
              )}
              {!isAuthenticated && <SFJoinButton />}
              {renderJoinButton()}
            </CardContent>
          </Card>
        )}

        {(group.myMembership || group.groupType === 'PUBLIC' || hasPermission('group:read:all')) && isAuthenticated && (
          <Tabs className="w-full" onValueChange={handleTabChange} value={activeTab}>
            <TabsList className="w-full max-w-full overflow-x-auto overflow-y-hidden" variant="tabs">
              <TabsTrigger
                ref={(el) => {
                  tabRefs.current['posts'] = el
                }}
                value="posts"
              >
                <MessageCircleIcon className="size-4" />
                {t('Posts')}
              </TabsTrigger>

              {group.postApproval && group.canModerate && (
                <TabsTrigger
                  ref={(el) => {
                    tabRefs.current['approvals'] = el
                  }}
                  value="approvals"
                >
                  <span className="flex items-center gap-2">
                    <MessageCircleQuestionIcon className="size-4" />
                    <span>{t('Approvals')}</span>
                    {group.postPendingApprovalCount > 0 ? (
                      <Badge size="sm">{limitBadgeNumber(group.postPendingApprovalCount)}</Badge>
                    ) : null}
                  </span>
                </TabsTrigger>
              )}

              <TabsTrigger
                ref={(el) => {
                  tabRefs.current['events'] = el
                }}
                value="events"
              >
                <CalendarCheckIcon className="size-4" />
                {t('Events')}
              </TabsTrigger>
            </TabsList>

            <TabsContent className="py-2 md:py-0" value="posts">
              {group.myMembership?.status === 'MUTED' && (
                <Alert className="mt-4 border-amber-500/40 bg-amber-500/10">
                  <AlertTitle>{t('Muted')}</AlertTitle>
                  <AlertDescription>
                    {t('Moderators of this group have muted you until')}
                    {dayjs(group?.myMembership?.mutedUntil).format('MMM D')}
                  </AlertDescription>
                </Alert>
              )}
              <Posts
                canApprove={group?.canModerate}
                disableInteraction={!(group.myMembership && group.canPost)}
                disablePoints={true}
                filterType="GROUP"
                groupId={group.id}
                noSideBars={true}
                showSort={true}
                showWhatsNew={Boolean(group.myMembership) || Boolean(group.canPost)}
              />
            </TabsContent>

            {group.postApproval && (
              <TabsContent className="py-2 md:py-0" value="approvals">
                <Posts
                  canApprove={group?.canModerate}
                  disableCreatePost={true}
                  filterType="GROUP"
                  groupId={group.id}
                  noSideBars={true}
                  showApprovalFilter={true}
                  showSort={true}
                  showWhatsNew={true}
                />
              </TabsContent>
            )}

            <TabsContent value="events">
              <GroupEventsList group={group} groupId={group.id} />
            </TabsContent>
          </Tabs>
        )}

        {isAuthenticated && (
          <>
            <GroupTransferModal
              groupId={group.id}
              onComplete={() => handleRefetch()}
              onOpenChange={(e) => setTransferModal(e.open)}
              open={transferModal}
            />

            <GroupDelete group={group} onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} />

            <ReportCreate
              onOpenChange={(e) => setReportModal(e.open)}
              open={reportModal}
              targetId={group.id}
              targetKind="GROUP"
            />

            <ConfirmDialog
              confirmText={t('Leave')}
              loading={leaveLoading}
              onConfirm={handleLeaveGroup}
              onOpenChange={(e) => setLeaveModal(e.open)}
              open={leaveModal}
              text={t(
                'Are you sure you want to leave this group? You will lose access to all group content and may need approval to rejoin.'
              )}
              title={t('Are you sure?')}
              variant="basic"
            />

            <ConfirmDialog
              confirmText={t('Decline')}
              loading={rejectInvitationLoading}
              onConfirm={handleRejectInvitation}
              onOpenChange={(e) => setDeclineInvitationModal(e.open)}
              open={declineInvitationModal}
              text={t('Are you sure you want to decline the invitation to join?')}
              title={t('Decline Invitation')}
              variant="basic"
            />
          </>
        )}
      </div>
    </div>
  )
}
